/**
 * BLOCKUSERPARAMETER writer — DYN-4 Phase 4a (Fix 3 symmetric emitter).
 *
 * Symmetric to `dxfBridge.dynamic.parser.userParam.ts`. Emits an
 * `ACDBBLOCKUSERPARAMETER` DXF object whose body matches the shape the
 * parser reads:
 *
 *   0   ACDBBLOCKUSERPARAMETER
 *   5   <self handle>
 *   330 <block record handle>            (owner)
 *   100 AcDbEvalExpr
 *   100 AcDbBlockElement
 *   100 AcDbBlockParameter
 *   100 AcDbBlock1PtParameter
 *   100 AcDbBlockUserParameter
 *   330 <associated variable handle>      (only after the subclass marker)
 *   301 <variable name>                   (secondary name slot)
 *   300 <primary name slot>
 *   170 <valueType code>                  (0=real, 1=distance, 2=area,
 *                                          3=angle, 5=string)
 *   40  <numeric default>                 (scalar types only)
 *   1   <string default>                  (string type only)
 *   280 <chainActions flag>
 *   281 <showGrip flag>
 *
 * When the user parameter carries no `associatedVariableHandle` (synthetic
 * in-memory instance not backed by an OBJECTS-section variable) we skip
 * the post-marker `330` so the parser falls through to its synthetic
 * defaults — never emit an empty/zero handle that would resolve to a
 * bogus AssocVariable.
 *
 * The writer is isolated in its own module rather than appended to
 * `dxfBridge.dynamic.writer.tier2.ts` (already 833 lines) per the
 * CLAUDE.md file-size guardrail (<500 lines ideal; BPT+constraint
 * writers land in their own siblings below).
 */
import type { DxfDynamicBlockObject, DxfGroup } from "@dxf-ai-editor/dxf-io";

import type { UserParameter } from "./dynamicBlock";
import { pushGroup } from "./dxfBridge.dynamic.shared";

/** Map UserParameter valueType → AutoCAD group-170 value type code. */
function valueTypeToCode(valueType: UserParameter["valueType"]): number {
  switch (valueType) {
    case "real":
      return 0;
    case "distance":
      return 1;
    case "area":
      return 2;
    case "angle":
      return 3;
    case "string":
      return 5;
    case "unknown":
    default:
      // Fall back to "real" for unknown — AutoCAD does not emit a 170
      // code for unknown, but zero matches the default in the parser.
      return 0;
  }
}

/**
 * Minimal writer context — the parameter writer only needs the owning
 * block-record handle (for group 330) and a fresh-handle factory.
 *
 * Shared shape with tier2 context; the `tableIdToHandle` map is not used
 * by this writer.
 */
export type UserParamWriterContext = {
  nextHandle: () => string;
  blockRecordHandle: string;
};

/**
 * Emit a single `ACDBBLOCKUSERPARAMETER` object for the given
 * `UserParameter`. The resulting object carries the full raw group list
 * so the top-level OBJECTS serializer re-emits it verbatim.
 */
export function writeUserParameter(
  p: UserParameter,
  ctx: UserParamWriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();

  pushGroup(groups, 0, "ACDBBLOCKUSERPARAMETER");
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlockParameter");
  pushGroup(groups, 100, "AcDbBlock1PtParameter");
  pushGroup(groups, 100, "AcDbBlockUserParameter");
  // Associated-variable reference — only emitted when we actually have a
  // handle. The parser anchors the lookup on the FIRST 330 after the
  // AcDbBlockUserParameter marker; emitting an empty/placeholder here
  // would point at a non-existent variable.
  if (p.associatedVariableHandle && p.associatedVariableHandle.length > 0) {
    pushGroup(groups, 330, p.associatedVariableHandle);
  }
  // Secondary name slot (AutoCAD emits both 301 and 300 with the same
  // value in АБИ1.dxf). Preserve that convention so the BPT reader that
  // keys by name still matches on either code.
  if (p.name) {
    pushGroup(groups, 301, p.name);
    pushGroup(groups, 300, p.name);
  }
  if (p.label) pushGroup(groups, 3, p.label);
  if (p.description) pushGroup(groups, 4, p.description);

  pushGroup(groups, 170, valueTypeToCode(p.valueType));

  // Default value — numeric on 40 for scalar types, string on 1 for
  // string-typed variables. Parsers accept either shape via the linked
  // ACDBASSOCVARIABLE; emitting a fallback here ensures a round-trip
  // without the variable still lands the value.
  if (p.valueType === "string") {
    const s =
      typeof p.defaultValue === "string"
        ? p.defaultValue
        : String(p.defaultValue);
    pushGroup(groups, 1, s);
  } else {
    const n =
      typeof p.defaultValue === "number"
        ? p.defaultValue
        : Number.parseFloat(String(p.defaultValue));
    if (Number.isFinite(n)) pushGroup(groups, 40, n);
  }

  pushGroup(groups, 280, p.chainActions ? 1 : 0);
  pushGroup(groups, 281, p.showGrip ? 1 : 0);

  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKUSERPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}
